<script setup lang="ts">
import InteractiveTopics from "~/components/InteractiveTopics.vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { computerSeoConfig } from "~/config/seo.config.computer";

const schemaLinks = [
  { name: "Paper Pattern", path: "/11th/computer/paper-pattern/" },
  { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
  { name: "Important Questions", path: "/11th/computer/important-questions/" },
  { name: "Guess Paper 2026", path: "/11th/computer/guess-paper/" },
  { name: "MCQs Top 100", path: "/11th/computer/mcqs/top-100/" },
  { name: "MCQ Chapter Pages", path: "/11th/computer/mcqs/" },
  { name: "Practice Mode", path: "/11th/computer/practice/mcqs/" },
  { name: "Notes", path: "/11th/computer/notes/" },
  { name: "Model Paper Practice", path: "/11th/computer/practice/" },
];

const heroLinks = [
  { label: "Paper Pattern", to: "/11th/computer/paper-pattern/" },
  { label: "Pairing Scheme", to: "/11th/computer/pairing-scheme/" },
  { label: "Important Questions", to: "/11th/computer/important-questions/" },
  { label: "MCQs", to: "/11th/computer/mcqs/top-100/" },
];

const studyPriority = [
  {
    label: "Critical",
    title: "Python (Chapter 2)",
    detail: "Highest weight aur sab se strong scoring chapter.",
    to: "/11th/computer/python/",
    tone: "border-amber-300 bg-gradient-to-br from-amber-100 via-orange-50 to-white dark:border-amber-800 dark:from-amber-950/40 dark:via-slate-950 dark:to-slate-900",
  },
  {
    label: "High",
    title: "Algorithms (Chapter 3)",
    detail: "Long aur short dono me strong support deta hai.",
    to: "/11th/computer/algorithm/",
    tone: "border-sky-300 bg-gradient-to-br from-sky-100 via-cyan-50 to-white dark:border-sky-800 dark:from-sky-950/40 dark:via-slate-950 dark:to-slate-900",
  },
  {
    label: "High",
    title: "Software Development (Chapter 1)",
    detail: "Core theory aur short questions ke liye important.",
    to: "/11th/computer/sdlc/",
    tone: "border-sky-300 bg-gradient-to-br from-sky-100 via-cyan-50 to-white dark:border-sky-800 dark:from-sky-950/40 dark:via-slate-950 dark:to-slate-900",
  },
  {
    label: "Medium",
    title: "Data Structures (Chapter 4)",
    detail: "Strong support chapter jo prep ko stabilize karta hai.",
    to: "/11th/computer/data-structures/",
    tone: "border-stone-300 bg-gradient-to-br from-stone-100 via-stone-50 to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
  {
    label: "Low",
    title: "Remaining Chapters",
    detail: "MCQs only route rakhein unless time zyada ho.",
    to: "/11th/computer/mcqs/",
    tone: "border-slate-300 bg-gradient-to-br from-slate-100 via-white to-white dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900",
  },
];

const preparationLinks = [
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/", description: "What comes in paper" },
  { title: "Important Questions", to: "/11th/computer/important-questions/", description: "What to prepare first" },
  { title: "Guess Paper", to: "/11th/computer/guess-paper/", description: "Final expected questions" },
];

const practiceLinks = [
  { title: "Top 100 MCQs", to: "/11th/computer/mcqs/top-100/", description: "Most important MCQs" },
  { title: "Chapter-wise MCQs", to: "/11th/computer/mcqs/", description: "MCQs by chapter" },
  { title: "Model Paper", to: "/11th/computer/practice/", description: "Full paper practice" },
];

const quickRevisionLinks = [
  { title: "Important Definitions", to: "/11th/computer/notes/", description: "Core terms aur quick theory revision." },
  { title: "High-yield Concepts", to: "/11th/computer/solved-exercise/", description: "Solved flow ke through high-yield concepts." },
  { title: "Python Quick Revision", to: "/11th/computer/python/notes/", description: "Variables, operators aur loops ki revision." },
];

const chapterLinks = [
  { label: "Ch 1 SDLC", to: "/11th/computer/sdlc/" },
  { label: "Ch 2 Python", to: "/11th/computer/python/" },
  { label: "Ch 3 Algorithms", to: "/11th/computer/algorithm/" },
  { label: "Ch 4 Data Structures", to: "/11th/computer/data-structures/" },
  { label: "Ch 5 Data Analytics", to: "/11th/computer/data-analytics/" },
  { label: "Ch 6 Emerging Tech", to: "/11th/computer/emerging-tech/" },
  { label: "Ch 7 Legal & Ethics", to: "/11th/computer/legal-ethics/" },
  { label: "Ch 8 Digital Literacy", to: "/11th/computer/digital-literacy/" },
  { label: "Ch 9 Entrepreneurship", to: "/11th/computer/entrepreneurship/" },
];

const mustDoLinks = [
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/" },
  { title: "Important Questions", to: "/11th/computer/important-questions/" },
  { title: "MCQs", to: "/11th/computer/mcqs/top-100/" },
  { title: "Guess Paper", to: "/11th/computer/guess-paper/" },
];

const faqs = [
  {
    question: "11th class computer ka paper kaise prepare karein?",
    answer: "Sab se pehle paper pattern samjhein, phir pairing scheme dekhein aur important questions prepare karein. Chapter 2 (Python), Chapter 3 aur Chapter 1 par focus karne se 60-70% paper cover ho jata hai.",
  },
  {
    question: "11th computer paper pattern 2026 kya hai?",
    answer: "Paper 75 marks ka hota hai jisme MCQs, short questions aur long questions hote hain. MCQs tamam chapters se aate hain jab ke long questions fixed chapters se hote hain.",
  },
  {
    question: "1st year computer pairing scheme kya hoti hai?",
    answer: "Pairing scheme yeh batati hai ke paper mein kis chapter se questions aayenge. Is se aapko pata chalta hai ke kis topic ko zyada prepare karna hai.",
  },
  {
    question: "11th computer important questions kaise milte hain?",
    answer: "Important questions pairing scheme aur past paper trends ke basis par select kiye jate hain. Inmein Python, Algorithms aur SDLC sab se zyada important hote hain.",
  },
  {
    question: "11th computer ke MCQs kis chapter se aate hain?",
    answer: "MCQs tamam chapters se aate hain lekin Python, Algorithms aur SDLC se zyada important MCQs aate hain.",
  },
  {
    question: "1st year computer guess paper 2026 reliable hai?",
    answer: "Guess paper exact paper nahi hota lekin pairing scheme aur past trends ke basis par banaya jata hai, is liye is se paper kaafi had tak cover ho jata hai.",
  },
  {
    question: "Kya sirf important chapters se pass ho sakte hain?",
    answer: "Haan, agar aap Chapter 2 (Python), Chapter 3 aur Chapter 1 strong kar lein to aap easily passing marks hasil kar sakte hain.",
  },
  {
    question: "Paper pattern, pairing scheme aur important questions mein kya difference hai?",
    answer: "Paper pattern paper ka structure batata hai, pairing scheme chapters ka distribution batati hai aur important questions un topics ka shortlist hote hain jo exam mein aane ke zyada chances rakhte hain.",
  },
];

usePageSeo({
  title: computerSeoConfig.titleTemplate("11th Class Computer Exam Prep 2026"),
  description: "11th class computer exam prep 2026 Punjab Board control center: paper pattern, pairing scheme, important questions, guess paper, MCQs and chapter-wise study flow.",
  keywords: "11th class computer exam prep, 11th class computer paper pattern, 11th class computer pairing scheme, 11th class computer important questions, 11th class computer mcqs",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Decision Engine",
    items: schemaLinks,
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Control Center</p>
      <h1 class="mt-3 max-w-4xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">
        11th Class Computer - Exam Prep 2026 (Punjab Board)
      </h1>
      <div class="mt-5 max-w-3xl rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-300">Start here:</p>
        <div class="mt-3 space-y-2 text-sm leading-7 text-slate-900 dark:text-slate-100">
          <p>- Paper Pattern samjhein</p>
          <p>- Pairing Scheme dekhein</p>
          <p>- Important Questions prepare karein</p>
        </div>
        <p class="mt-4 text-base font-semibold text-emerald-700">Sirf Chapter 2, 3, 1 strong karein -> 70% paper cover</p>
      </div>

      <div class="mt-6 flex flex-wrap gap-3">
        <NuxtLink
          v-for="item in heroLinks"
          :key="item.to"
          :to="item.to"
          class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700"
        >
          {{ item.label }}
        </NuxtLink>
      </div>
    </section>

    <InteractiveTopics
      eyebrow="Python Chapter 2"
      title="Python Topic Practice"
      intro="Variables, data types, operators, loops, functions aur programs ko topic-wise practice karein. Concept samjho aur phir exam pages par jao."
    />

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">What To Study</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Shortcut priority</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <NuxtLink
          v-for="item in studyPriority"
          :key="item.title"
          :to="item.to"
          :class="['rounded-[22px] border p-4', item.tone]"
        >
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{{ item.label }}</p>
          <h3 class="mt-2 text-lg font-semibold tracking-tight text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">{{ item.detail }}</p>
          <p class="mt-3 text-sm font-semibold text-sky-700 dark:text-sky-400">Open chapter -></p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Paper Pattern</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Paper Pattern (How Exam Works)</h2>
        </div>
        <NuxtLink to="/11th/computer/paper-pattern/" class="text-sm font-semibold text-sky-700 hover:text-sky-800">
          View Full Paper Pattern ->
        </NuxtLink>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Paper ka structure samajhne ke liye.</p>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          to="/11th/computer/mcqs/"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">MCQs (All Chapters)</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">All chapters se MCQs practice karein</p>
          <p class="mt-3 text-sm font-semibold text-sky-700">Practice MCQs -></p>
        </NuxtLink>
        <NuxtLink
          to="/11th/computer/pairing-scheme/"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">Short Questions (Blocks)</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">Q2, Q3, Q4 blocks samjhein</p>
          <p class="mt-3 text-sm font-semibold text-sky-700">View pairing -></p>
        </NuxtLink>
        <NuxtLink
          to="/11th/computer/pairing-scheme/"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">Long Questions (Fixed Chapters)</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">Fixed chapters se long questions aate hain</p>
          <p class="mt-3 text-sm font-semibold text-sky-700">See important chapters -></p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">What To Prepare</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">What to Prepare (Start Here)</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Sab se important cheezain yahan se start karein.</p>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in preparationLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Practice</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Practice (MCQs & Model Papers)</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">MCQs aur model paper se practice karein.</p>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in practiceLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Quick Revision</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Quick Revision (Last Day Prep)</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">Last day quick revision ke liye.</p>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in quickRevisionLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Chapter-wise Study</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Chapter-wise Study (11th Computer)</h2>
      </div>
      <div class="mt-5 flex flex-wrap gap-3">
        <NuxtLink
          v-for="item in chapterLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-900 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          {{ item.label }}
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-slate-950 p-5 text-white md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Must Do</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight">Do these before exam</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <NuxtLink
          v-for="item in mustDoLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-white/10 bg-white/5 p-5 transition hover:bg-white/10"
        >
          <h3 class="text-lg font-semibold text-white">{{ item.title }}</h3>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer Exam Prep FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>
  </PageContainer>
</template>
